import { useState, useMemo } from 'react';
import { usePortfolio } from '../context/PortfolioContext';

function HoldingsPage() {
  const { holdings, isLoading } = usePortfolio();
  const [sortKey, setSortKey] = useState('market_value_gbp');
  const [sortDir, setSortDir] = useState('desc');

  const sortedHoldings = useMemo(() => {
    if (!holdings) return [];
    return [...holdings].sort((a, b) => {
      const aVal = a[sortKey] || 0;
      const bVal = b[sortKey] || 0;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [holdings, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const formatCurrency = (value, decimals = 2, currency = 'GBP') => {
    if (value === null || value === undefined || value === '') return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '') return '-';
    return Number(value).toFixed(decimals);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value) * 100;
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
  };

  if (isLoading && !holdings?.length) {
    return <div className="loading-screen"><div className="loading-spinner"></div></div>;
  }

  return (
    <div>
      <div className="data-table-container">
        <div className="data-table-header">
          <span className="data-table-title">Active Positions</span>
          <span className="data-table-badge">
            {sortedHoldings.filter(h => h.quantity > 0).length} positions
          </span>
        </div>
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('ticker')}>Ticker {sortKey === 'ticker' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('quantity')}>Shares {sortKey === 'quantity' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th>Avg Price</th>
                <th>Current Price</th>
                <th>P/E</th>
                <th>Value (Local)</th>
                <th onClick={() => handleSort('market_value_gbp')}>Value (GBP) {sortKey === 'market_value_gbp' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th>FX Impact (GBP)</th>
                <th>P&L (GBP)</th>
                <th>Return %</th>
                <th>Weight %</th>
              </tr>
            </thead>
            <tbody>
              {sortedHoldings
                .filter(h => h.quantity > 0)
                .map(holding => {
                  const totalValue = holdings.reduce((sum, h) => sum + (h.market_value_gbp || 0), 0);
                  const weight = totalValue > 0 ? ((holding.market_value_gbp || 0) / totalValue) * 100 : 0;
                  const pnl = (holding.unrealized_gbp || 0) + (holding.realized_gbp || 0);
                  const cost = (holding.quantity || 0) * (holding.avg_cost_gbp || 0);
                  const returnPct = cost > 0 ? pnl / cost : 0;
                  
                  return (
                    <tr key={holding.ticker}>
                      <td>
                        <strong>{holding.ticker?.toUpperCase()}</strong>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {holding.display_name}
                        </div>
                      </td>
                      <td>{formatNumber(holding.quantity)}</td>
                      <td>{holding.quantity > 0 ? formatCurrency(holding.avg_price_local, 2, holding.currency) : '-'}</td>
                      <td>{holding.quantity > 0 ? formatCurrency(holding.current_price_local, 2, holding.currency) : '-'}</td>
                      <td>{holding.pe_ratio ? holding.pe_ratio.toFixed(1) : '-'}</td>
                      <td>{holding.quantity > 0 ? formatCurrency(holding.market_value_local, 0, holding.currency) : '-'}</td>
                      <td>{formatCurrency(holding.market_value_gbp)}</td>
                      <td className={(holding.fx_impact_gbp || 0) >= 0 ? 'pos' : 'neg'}>
                        {formatCurrency(holding.fx_impact_gbp)}
                      </td>
                      <td className={pnl >= 0 ? 'pos' : 'neg'}>
                        {formatCurrency(pnl)}
                      </td>
                      <td className={returnPct >= 0 ? 'pos' : 'neg'}>
                        {formatPercent(returnPct)}
                      </td>
                      <td>{weight.toFixed(1)}%</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default HoldingsPage;
